import { z } from "zod";
import { InquiryKind, InquirySource, InquiryStatus, IsoDateTime, paged, PageQuery, Uuid } from "./common.ts";

/*
 * Inquiries (UC-30, ADR-040, ADR-050 Stufe 1, ADR-093, 06 `inquiry`): from prospects on the
 * website and guests in the portal (`access`), applications for the beta from the website
 * (`application`), and from customers in the portal (`support`, `callback`, `workflow`). Every one
 * goes to `POST /api/inquiries` on the portal host; the record is kept and forwarded by mail to
 * the team, who handle it outside the platform. The API sets `source` from the checked origin,
 * never the sender (ADR-093, `bff-prinzip.md`).
 */

/** What every kind says; the callback time and the details of a workflow go here too (ADR-093 B.2). */
const InquiryMessage = z.string().trim().min(1).max(4000);

/**
 * A name or company of the access form: one line, at most 200 characters (N3, ADR-135 point 10).
 * Both go into the subject of the team's mail; a line break there could start a second header. CR,
 * LF and the Unicode line and paragraph separators are refused with 400, after trimming.
 */
const InquiryLine = z
  .string()
  .trim()
  .max(200)
  .regex(/^[^\r\n\p{Zl}\p{Zp}]*$/u, "one line, without a line break");

/**
 * The product the inquiry is about, preselected on a product page; optional for every kind. Any
 * product the catalogue shows, one on request included (ADR-112 point 6); another is `400`.
 */
const InquiryProduct = Uuid.optional();

/**
 * A phone number as the customer writes it, kept as written (trimmed) because the team calls it:
 * 6 to 30 characters of digits, spaces and `+ ( ) / -`, at least six of them digits (TL,
 * 2026-10-12). PII, deleted with the inquiry (06 §5).
 */
export const PhoneNumber = z
  .string()
  .trim()
  .min(6)
  .max(30)
  .regex(/^[0-9 +()/-]+$/, "a phone number has digits, spaces and + ( ) / - only")
  .refine((value) => (value.match(/\d/g) ?? []).length >= 6, "a phone number has at least six digits");

/**
 * Minimum time between showing the access form and sending it (ADR-050, TL 2026-10-12): the API
 * refuses a faster one with 400. In the contract, so the portal's mock checks the same time and a
 * test waits it out (M16 of the review 2026-10-19).
 */
export const INQUIRY_MIN_FILL_MS = 3_000;

/**
 * `kind = access`, the inquiry of a prospect or guest, without a session; a body without `kind`
 * is this one, so the website's form stays as it is. Protection against bots in stage 1 of
 * ADR-050, both server-side: `website` is the honeypot field — rendered invisible, it must stay
 * empty; `startedAt` is when the form was shown, the API rejects submissions faster than
 * `INQUIRY_MIN_FILL_MS`. Rate limiting per origin comes on top (07 §2.2). The two names are part
 * of the contract so the form in `packages/ui` uses the same ones in both apps.
 *
 * The product comes as the platform's `productId` (the portal) or as its dataCycle id,
 * `productDatacycleId` (the website, whose products come from dataCycle through the gateway; the
 * API finds the product by it with the catalogue's rule; TL 2026-10-21, addendum to ADR-093). Not
 * both: `InquiryCreateRequest` refuses that, this object stays without a refinement so
 * `packages/ui` can derive its form from it (`.omit` is refused on a refined object in Zod 4).
 */
export const AccessInquiryRequest = z.object({
  kind: z.literal("access").default("access"),
  name: InquiryLine.optional(),
  email: z.email(),
  company: InquiryLine.optional(),
  message: InquiryMessage,
  productId: InquiryProduct,
  /** the product by its dataCycle id (`product.datacycle_id`), from the website's product page */
  productDatacycleId: Uuid.optional(),
  /** honeypot: must be empty */
  website: z.literal("").optional(),
  /** when the form was rendered; the minimum fill time is checked against it */
  startedAt: IsoDateTime,
});
export type AccessInquiryRequest = z.infer<typeof AccessInquiryRequest>;

/*
 * The kinds of a customer's session (`inquiry.create`, A and K): name, e-mail, company and
 * customer come from the session, so the body carries none of them, and neither honeypot nor
 * minimum time (ADR-093 point 3). Fields of the access form sent along are dropped.
 */

/** `kind = support`: a question or a problem. */
export const SupportInquiryRequest = z.object({
  kind: z.literal("support"),
  message: InquiryMessage,
  productId: InquiryProduct,
});
export type SupportInquiryRequest = z.infer<typeof SupportInquiryRequest>;

/** `kind = callback`: the team calls back; the phone number is required, the time goes into the message. */
export const CallbackInquiryRequest = z.object({
  kind: z.literal("callback"),
  message: InquiryMessage,
  phone: PhoneNumber,
  productId: InquiryProduct,
});
export type CallbackInquiryRequest = z.infer<typeof CallbackInquiryRequest>;

/** `kind = workflow`: a workflow built by the team; handled like `support` in the alpha release (ADR-093 B.1). */
export const WorkflowInquiryRequest = z.object({
  kind: z.literal("workflow"),
  message: InquiryMessage,
  productId: InquiryProduct,
});
export type WorkflowInquiryRequest = z.infer<typeof WorkflowInquiryRequest>;

/*
 * `kind = application`, the application for the beta (ADR-143, addendum to ADR-093
 * and ADR-133 point 1): the website's form asks more than the access form, and what it asks stays
 * with the inquiry as `details` (06 `inquiry.details`, jsonb). The form may change after the beta,
 * so the details carry their version `v`; a later version is added to `ApplicationDetails` as a
 * union, the stored ones stay readable until they are deleted with their inquiry. Fixed choices are
 * codes; website, portal and mail hold their labels.
 */

/** Where the applicant is: `IT` is South Tyrol on the form. */
export const ApplicationCountry = z.enum(["AT", "DE", "CH", "IT", "other"]);
export type ApplicationCountry = z.infer<typeof ApplicationCountry>;

/** Whether the applicant already uses dataCycle. */
export const DatacycleUse = z.enum(["yes", "no", "unknown"]);
export type DatacycleUse = z.infer<typeof DatacycleUse>;

/** How many people are to work with the AI Cockpit. */
export const TeamSize = z.enum(["1-5", "6-15", "16-50", "50+"]);
export type TeamSize = z.infer<typeof TeamSize>;

/** Which areas are to use it; several at once. */
export const ApplicationArea = z.enum(["content", "marketing", "guest_service", "data", "management", "other"]);
export type ApplicationArea = z.infer<typeof ApplicationArea>;

/** A first or last name: one line like `InquiryLine`, 1 to 100 characters, so both fit `name`. */
const ApplicationName = InquiryLine.min(1).max(100);

/** What the form asks besides the columns of `inquiry`, version 1, as stored and as staff read it. */
export const ApplicationDetails = z.object({
  v: z.literal(1),
  firstName: ApplicationName,
  lastName: ApplicationName,
  /** function or role at the organisation */
  jobTitle: InquiryLine.min(1),
  country: ApplicationCountry,
  datacycleUse: DatacycleUse,
  teamSize: TeamSize,
  areas: z.array(ApplicationArea).refine((areas) => new Set(areas).size === areas.length, "each area once"),
  /** ready to give feedback in the first phase */
  feedbackReady: z.boolean(),
  /** news by e-mail; in the alpha release only stored, deleted with the inquiry (TL, 2026-10-27) */
  newsletter: z.boolean(),
});
export type ApplicationDetails = z.infer<typeof ApplicationDetails>;

/**
 * `kind = application`, without a session like `access`, with honeypot, minimum time and the same
 * rate limits. The details come flat, as the form has them; the API stores them with `v` and
 * composes `name` from first and last name. The organisation is required, the phone optional, the
 * message is the goal ("Was möchtet ihr als Erstes erleichtern?") and optional, at most 2000
 * characters. Without the accepted privacy notice the API answers `400`; the acceptance itself is
 * not stored (TL, 2026-10-27).
 */
export const ApplicationInquiryRequest = z.object({
  kind: z.literal("application"),
  firstName: ApplicationName,
  lastName: ApplicationName,
  jobTitle: InquiryLine.min(1),
  email: z.email(),
  phone: PhoneNumber.optional(),
  company: InquiryLine.min(1),
  country: ApplicationCountry,
  datacycleUse: DatacycleUse,
  teamSize: TeamSize,
  areas: ApplicationDetails.shape.areas.default([]),
  message: z.string().trim().min(1).max(2000).optional(),
  feedbackReady: z.boolean().default(false),
  newsletter: z.boolean().default(false),
  privacyAccepted: z.literal(true, "the privacy notice must be accepted"),
  /** honeypot: must be empty */
  website: z.literal("").optional(),
  /** when the form was rendered; the minimum fill time is checked against it */
  startedAt: IsoDateTime,
});
export type ApplicationInquiryRequest = z.infer<typeof ApplicationInquiryRequest>;

/**
 * `POST /api/inquiries`, one body per `kind` (ADR-093). `access` and `application` go without a
 * session; the others need a customer's session and `inquiry.create`, otherwise `401` or `403
 * role_required`. Answered with `201` and the id. An access inquiry names its product by one id,
 * `productId` or `productDatacycleId`; both are refused at `productDatacycleId`.
 */
export const InquiryCreateRequest = z
  .discriminatedUnion("kind", [
    AccessInquiryRequest,
    SupportInquiryRequest,
    CallbackInquiryRequest,
    WorkflowInquiryRequest,
    ApplicationInquiryRequest,
  ])
  .refine((body) => body.kind !== "access" || body.productId === undefined || body.productDatacycleId === undefined, {
    message: "the product by productId or by productDatacycleId, not both",
    path: ["productDatacycleId"],
  });
export type InquiryCreateRequest = z.infer<typeof InquiryCreateRequest>;

/** `201` with the id; nothing else leaves, the sender gets no view of inquiries. */
export const InquiryCreatedResponse = z.object({
  id: Uuid,
});
export type InquiryCreatedResponse = z.infer<typeof InquiryCreatedResponse>;

/** An inquiry as staff see it (`GET /api/staff/inquiries`). */
export const Inquiry = z.object({
  id: Uuid,
  kind: InquiryKind,
  /** from the form for `access`, first and last name for `application`, from the session for the other kinds */
  name: z.string().nullable(),
  email: z.email(),
  company: z.string().nullable(),
  /** null only for an `application` without a goal */
  message: z.string().nullable(),
  /** set for `callback`, and for `application` when the applicant gave one */
  phone: z.string().nullable(),
  productId: Uuid.nullable(),
  /** set for the kinds of a customer's session, and for `access` when the sender was logged in */
  customerId: Uuid.nullable(),
  /** from the checked origin of the request, never from the sender */
  source: InquirySource,
  status: InquiryStatus,
  forwardedAt: IsoDateTime.nullable(),
  /** what the application form asked besides the columns; set for `application` only */
  application: ApplicationDetails.nullable(),
  /** deletion after six months without a customer relation (F-61) */
  deleteAfter: IsoDateTime,
  createdAt: IsoDateTime,
});
export type Inquiry = z.infer<typeof Inquiry>;

/**
 * `GET /api/staff/inquiries`, filtered by status and kind (AP-F8.4) and, for the customer file, by
 * the customer (ADR-151): the inquiries from that customer's session. One without a
 * customer, a guest's or an application, is never assigned by e-mail or company.
 */
export const InquiryListQuery = PageQuery.extend({
  status: InquiryStatus.optional(),
  kind: InquiryKind.optional(),
  customerId: Uuid.optional(),
});
export type InquiryListQuery = z.infer<typeof InquiryListQuery>;

export const InquiryListResponse = paged(Inquiry);
export type InquiryListResponse = z.infer<typeof InquiryListResponse>;

/**
 * `PUT /api/staff/inquiries/:id`: forwarded to sales, or closed. Forward only: `new` to either,
 * `forwarded` to `closed`; a closed inquiry is refused with `inquiry_closed`, the same status
 * again writes nothing. `forwarded` stamps `forwardedAt`.
 */
export const InquiryUpdateRequest = z.object({
  status: z.enum(["forwarded", "closed"]),
});
export type InquiryUpdateRequest = z.infer<typeof InquiryUpdateRequest>;
